import React from 'react';
import { createStyles } from '@/contexts/theme-context';
import { Skeleton } from '@usepepper/sprout-kit-ui';
import { StyleSheet, View } from 'react-native';

const TITLE_SKELETON_HEIGHT = 14;
const TITLE_SKELETON_WIDTH = 64;
const ROW_SKELETON_HEIGHT = 12;
const TITLE_ROW_HEIGHT = 40;
const ROW_HEIGHT = 21;

const TOP_ROWS_COUNT = 2;
const TOP_ROWS_OPACITY = 0.5;
const BOTTOM_ROWS_OPACITY = 0.3;

interface SkeletonRowSpec {
  left: `${number}%`;
  right: `${number}%` | null;
}

const SKELETON_ROWS: ReadonlyArray<SkeletonRowSpec> = [
  { left: '15%', right: '10%' },
  { left: '15%', right: '10%' },
  { left: '25%', right: '10%' },
  { left: '25%', right: '10%' },
];

/**
 * Shared section skeleton for the item-detail loading states — a title row plus
 * a card of four label/value rows.
 */
export const ItemDetailSectionSkeleton: React.FC = () => {
  const [styles, theme] = useStyles();
  return (
    <View style={styles.sectionSkeleton}>
      <View style={styles.sectionSkeletonTitleRow}>
        <Skeleton
          borderRadius={theme.sk.radii.radiusLarge}
          height={TITLE_SKELETON_HEIGHT}
          width={TITLE_SKELETON_WIDTH}
        />
      </View>
      <View style={styles.sectionSkeletonCard}>
        {SKELETON_ROWS.map((row, index) => (
          <View
            key={index}
            style={StyleSheet.flatten([
              styles.sectionSkeletonRow,
              {
                opacity:
                  index < TOP_ROWS_COUNT
                    ? TOP_ROWS_OPACITY
                    : BOTTOM_ROWS_OPACITY,
              },
            ])}
          >
            <Skeleton
              borderRadius={theme.sk.radii.radiusLarge}
              height={ROW_SKELETON_HEIGHT}
              width={row.left}
            />
            {row.right !== null ? (
              <Skeleton
                borderRadius={theme.sk.radii.radiusLarge}
                height={ROW_SKELETON_HEIGHT}
                width={row.right}
              />
            ) : null}
          </View>
        ))}
      </View>
    </View>
  );
};

const useStyles = createStyles((theme) => ({
  sectionSkeleton: {
    flexDirection: 'column',
  },
  sectionSkeletonCard: {
    backgroundColor: theme.sk.color.bg.primary.default,
    borderColor: theme.sk.color.border.primary,
    borderRadius: theme.sk.radii.radiusMedium,
    borderWidth: 1,
    flexDirection: 'column',
    gap: theme.sk.spacing.spaceXsmall,
    paddingHorizontal: theme.sk.spacing.spaceMedium,
    paddingVertical: theme.sk.spacing.spaceSMedium,
  },
  sectionSkeletonRow: {
    alignItems: 'center',
    flexDirection: 'row',
    height: ROW_HEIGHT,
    justifyContent: 'space-between',
  },
  sectionSkeletonTitleRow: {
    alignItems: 'center',
    flexDirection: 'row',
    height: TITLE_ROW_HEIGHT,
  },
}));
